import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect } from 'react';

import {
  Card,
  Table,
  TableRow,
  useTheme,
  Accordion,
  TableCell,
  TableBody,
  TableContainer,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useToast } from 'src/hooks/useToast';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';
import { deleteTicket, multipleTicketDelete } from 'src/services/ticket.service';

import TableLoader from 'src/components/loaders/Loader';
import Scrollbar from 'src/components/scrollbar/Scrollbar';
import TableToolbar from 'src/components/ToolBars/TableToolbar';
import TableHeaders from 'src/components/tableHeaders/TableHeaders';
import TablePaginations from 'src/components/table-pagination/TablePaginations';

import TicketsTableBody from './tableBody';
import AdvancedFilter from './advancedFilter';
import { encrypt } from '../../../utils/hashCode';
import { ALL_TICKET_TABLE_HEAD } from '../tableHeads';
import FilterPopover from '../popovers/filterPopover';
import OptionsPopover from '../popovers/optionsPopover';

export default function TicketsTable({
  isAccordionOpen,
  rowsPerPage,
  setRowsPerPage,
  statusesList,
  setOpenModifyTicket,
  order,
  setOrder,
  orderBy,
  setOrderBy,
  filterTitle,
  setFilterTitle,
  searchBy,
  setSearchBy,
  filterBy,
  setFilterBy,
  searchValue,
  setSearchValue,
  departmentData,
  tagsData,
  page,
  setPage,
  isLoading,
  currentPageTickets,
  filteredTickets,
  getTicketsByDateRange,
  setSelectedItem,
  selectedItem,
  ticketsWithStatus,
  setFilterByData,
}) {
  const { t } = useTranslation();
  const { isSmallScreen } = useResponsiveScreen();

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const { id: currentUserId, permissions } = useAuth();
  const { showToast, hideToast } = useToast();
  const navigate = useNavigate();

  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [selected, setSelected] = useState([]);
  const [showDelete, setShowDelete] = useState(true);

  // States to control the ticket filter popover opening and nested selections
  const [filterPopover, setFilterPopover] = useState(null);
  const [subFilterPopover, setSubFilterPopover] = useState({
    department: null,
    status: null,
    priority: null,
    tag: null,
  });

  const handleOpenFilter = (event) => {
    setFilterPopover(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterPopover(null);
  };

  const handleCloseSubFilter = () => {
    setSubFilterPopover({
      department: null,
      status: null,
      priority: null,
      tag: null,
    });
  };

  const canUpdate = permissions?.includes('TICKET:UPDATE');
  const canDelete = permissions?.includes('TICKET:DELETE');
  const showOptionsColumn = canUpdate || canDelete;

  const dynamicHeaders = useMemo(() => {
    if (showOptionsColumn) return ALL_TICKET_TABLE_HEAD;
    return ALL_TICKET_TABLE_HEAD.filter(
      (header) =>
        header.id !== 'options' &&
        header.id !== 'Options' &&
        header.label !== 'Options' &&
        header.label !== 'options'
    );
  }, [showOptionsColumn]);

  // --- LOGIC HANDLERS ---

  const handleSelectAllClick = () => {
    const pageIds = (currentPageTickets || []).map((n) => n.id);
    if (pageIds.every((id) => selected.includes(id))) {
      setSelected(selected.filter((id) => !pageIds.includes(id)));
    } else {
      setSelected([...new Set([...selected, ...pageIds])]);
    }
  };

  const handleOpenMenu = (event, row) => {
    setPopoverAnchorEl(event.currentTarget);
    setSelectedItem(row);

    if (!canDelete && row.createdBy !== currentUserId) {
      setShowDelete(false);
    } else {
      setShowDelete(true);
    }
  };

  const removeTicket = async (id) => {
    genericApiToaster(
      deleteTicket,
      id,
      t('successMessages.successTicketRemove'),
      t('errorMessages.errorDeletingTicket'),
      showToast,
      hideToast,
      t,
      () => {
        getTicketsByDateRange();
        setSelected([]);
        setPopoverAnchorEl(null);
      }
    );
  };

  const deleteMultipleTickets = async (ticketIds) => {
    genericApiToaster(
      multipleTicketDelete,
      [ticketIds],
      '',
      t('errorMessages.errorDeletingTicket'),
      showToast,
      hideToast,
      t,
      (res) => {
        showToast({
          message: t('successMessages.succesMultipleTicketsDelte', {
            ticketsDeleted: res.deletedCount,
            totalTickets: ticketIds.length,
          }),
          severity: 'success',
        });
        getTicketsByDateRange();
        setSelected([]);
      }
    );
  };

  useEffect(() => {}, [currentPageTickets, selected]);

  return (
    <Card
      sx={{
        mt: '10px',
        borderRadius: '24px',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: 'none',
      }}
    >
      <Accordion
        expanded={isAccordionOpen}
        sx={{
          bgcolor: 'transparent',
          boxShadow: 'none',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary sx={{ display: 'none' }} />
        <AccordionDetails sx={{ p: 0 }}>
          <AdvancedFilter
            ticketsWithStatus={ticketsWithStatus || []}
            setFilterByData={setFilterByData}
            onClose={() => {}}
          />
        </AccordionDetails>
      </Accordion>

      <TableToolbar
        numSelected={selected.length}
        filterName={searchValue}
        setFilterName={setSearchValue}
        filterBy={searchBy}
        setFilterBy={setSearchBy}
        setPage={setPage}
        handleOpenFilter={handleOpenFilter}
        handleCloseFilter={handleCloseFilter}
        filterValue={filterTitle}
        setFilterValue={setFilterTitle}
        onFilterName={(e) => {
          setPage(0);
          setSearchValue(e.target.value);
        }}
        deleteFunc={canDelete ? deleteMultipleTickets : undefined}
        selected={selected}
        handleRemoveSelected={() => setSelected([])}
        model="Ticket"
        departmentsData={departmentData}
      />

      <Scrollbar>
        <TableContainer
          sx={{
            '&::-webkit-scrollbar': {
              width: '6px',
              height: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: isDark
                ? 'rgba(255, 255, 255, 0.15)'
                : 'rgba(0, 0, 0, 0.15)',
              borderRadius: '10px',
              '&:hover': {
                backgroundColor: isDark
                  ? 'rgba(255, 255, 255, 0.3)'
                  : 'rgba(0, 0, 0, 0.3)',
              },
            },
          }}
        >
          <Table size={isSmallScreen ? 'small' : 'medium'}>
            <TableHeaders
              order={order}
              orderBy={orderBy}
              headLabel={dynamicHeaders}
              rowCount={(filteredTickets || []).length}
              numSelected={selected.length}
              onRequestSort={(e, p) => {
                const isAsc = orderBy === p && order === 'asc';
                setOrder(isAsc ? 'desc' : 'asc');
                setOrderBy(p);
              }}
              onSelectAllClick={handleSelectAllClick}
              showCheckBox={canDelete}
            />
            {isLoading ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={14} sx={{ textAlign: 'center' }}>
                    <TableLoader size={100} />
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TicketsTableBody
                currentPageTickets={currentPageTickets || []}
                handleOpenMenu={handleOpenMenu}
                handleClick={(e, id) =>
                  setSelected(
                    selected.includes(id)
                      ? selected.filter((x) => x !== id)
                      : [...selected, id]
                  )
                }
                handleClickRow={(r) => navigate(`/tickets/${encrypt(r.id)}`)}
                selected={selected}
                canUpdate={canUpdate}
                canDelete={canDelete}
                showCheckBox={canDelete}
                showOptions={showOptionsColumn}
              />
            )}
          </Table>
        </TableContainer>
      </Scrollbar>

      <TablePaginations
        filteredEntity={filteredTickets || []}
        handleChangePage={(e, n) => setPage(n)}
        handleChangeRowsPerPage={(e) => {
          setPage(0);
          setRowsPerPage(parseInt(e.target.value, 10));
        }}
        page={page}
        rowsPerPage={rowsPerPage}
      />

      <OptionsPopover
        removeTicket={removeTicket}
        selectedItem={selectedItem}
        popoverAnchorEl={popoverAnchorEl}
        handleCloseMenu={() => setPopoverAnchorEl(null)}
        setOpenModifyTicket={setOpenModifyTicket}
        showDelete={showDelete && canDelete}
        canUpdate={canUpdate}
      />

      <FilterPopover
        setSubFilterPopover={setSubFilterPopover}
        subFilterPopover={subFilterPopover}
        handleCloseSubFilter={handleCloseSubFilter}
        filterPopover={filterPopover}
        handleCloseFilter={handleCloseFilter}
        setPage={setPage}
        setFilterTitle={setFilterTitle}
        setFilterBy={setFilterBy}
        setSearchBy={setSearchBy}
        departmentData={departmentData}
        statusesList={statusesList}
        tagsData={tagsData}
      />
    </Card>
  );
}

TicketsTable.propTypes = {
  isAccordionOpen: PropTypes.bool,
  rowsPerPage: PropTypes.number.isRequired,
  setRowsPerPage: PropTypes.func.isRequired,
  statusesList: PropTypes.array,
  setOpenModifyTicket: PropTypes.func,
  order: PropTypes.string,
  setOrder: PropTypes.func,
  orderBy: PropTypes.string,
  setOrderBy: PropTypes.func,
  filterTitle: PropTypes.string,
  setFilterTitle: PropTypes.func,
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  searchBy: PropTypes.string,
  setSearchBy: PropTypes.func,
  filterBy: PropTypes.string,
  setFilterBy: PropTypes.func,
  searchValue: PropTypes.string,
  setSearchValue: PropTypes.func,
  departmentData: PropTypes.array,
  tagsData: PropTypes.array,
  currentPageTickets: PropTypes.array.isRequired,
  filteredTickets: PropTypes.array.isRequired,
  getTicketsByDateRange: PropTypes.func.isRequired,
  setSelectedItem: PropTypes.func.isRequired,
  ticketsWithStatus: PropTypes.array,
  setFilterByData: PropTypes.func,
  selectedItem: PropTypes.object,
};
